import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CountUp from '../../effects/CountUp/CountUp';
import ProjectIcon from '../../ProjectIcon/ProjectIcon';
import ContactSection from '../../site/ContactSection/ContactSection';
import Icon from '../../ui/Icon/Icon';
import LabPill from '../../ui/LabPill/LabPill';
import PillLink from '../../ui/PillLink/PillLink';
import Section from '../../ui/Section/Section';
import BrowserFrame from '../BrowserFrame/BrowserFrame';
import NextProject from '../NextProject/NextProject';
import ProjectVisual from '../ProjectVisual/ProjectVisual';
import { findNeighbourProjects, linkHost, plainProjectName, type Project, type ProjectLink } from '../../../data/projects';
import { userCountAffixes, userReach } from '../../../data/reach';
import { useProjectCopy } from '../../../hooks/useProjectCopy';
import { usePageMeta } from '../../../hooks/usePageMeta';
import { usePlatformLabel } from '../../../hooks/usePlatformLabel';
import { useLanguage } from '../../../i18n/useLanguage';
import styles from './ProjectDetail.module.css';

const LONG_NAME_LENGTH = 20;
const SINGLE_LANGUAGE = 1;

function useLinkLabel(): (link: ProjectLink) => string {
    const { t } = useTranslation();
    return (link: ProjectLink) => (link.kind === 'website' ? linkHost(link) : t(`links.${link.kind}`));
}

export default function ProjectDetail({ project }: { project: Project }) {
    const { t } = useTranslation();
    const { language, localizedPath } = useLanguage();
    const copy = useProjectCopy()(project.slug);
    const users = userReach(project);
    const platformLabel = usePlatformLabel();
    const linkLabel = useLinkLabel();
    const plainName = plainProjectName(project);
    const websiteLink = project.links.find((link) => link.kind === 'website');
    const showsLanguages = !users && project.localeCount > SINGLE_LANGUAGE;

    usePageMeta(t('meta.projectTitle', { name: plainName, category: copy.category }), copy.description);

    return (
        <>
            <section className={styles.hero} data-theme="dark" aria-labelledby="project-title">
                <div className={`page-inner ${styles.heroInner}`}>
                    <Link to={localizedPath('/work')} className={styles.back}>
                        <Icon name="arrowLeft" className={styles.backIcon} />
                        {t('project.back')}
                    </Link>
                    <h1 id="project-title" className={styles.lockup}>
                        <ProjectIcon project={project} size="large" />
                        <span className={plainName.length > LONG_NAME_LENGTH ? `${styles.title} ${styles.longTitle}` : styles.title}>{project.name}</span>
                    </h1>
                    <div className={styles.categoryRow}>
                        <p className={styles.category}>{copy.category}</p>
                        {project.ownership === 'lab' && <LabPill />}
                    </div>
                    <p className={styles.tagline}>{copy.summary}</p>
                    {project.links.length > 0 && (
                        <div className={styles.links}>
                            {project.links.map((link, index) => (
                                <PillLink key={link.href} href={link.href} variant={index === 0 ? 'solid' : 'glass'} icon="arrowUpRight">
                                    {linkLabel(link)}
                                </PillLink>
                            ))}
                        </div>
                    )}
                </div>
                <div className={`page-inner ${styles.visual}`}>
                    <ProjectVisual project={project} format="hero" screenAlt={copy.screenAlt} />
                </div>
            </section>

            <Section theme="light" shape="sheet" labelledBy="overview-title">
                <div className={`page-inner ${styles.overview}`}>
                    <div>
                        <h2 id="overview-title" className="visually-hidden">{t('project.facts.title')}</h2>
                        <p className={styles.description}>{copy.description}</p>
                    </div>
                    <dl className={styles.facts}>
                        {users && (
                            <div className={`${styles.fact} ${styles.headlineFact}`}>
                                <dt>{t('project.facts.users')}</dt>
                                <dd className={styles.factNumber}><CountUp to={users.count} language={language} {...userCountAffixes(users)} /></dd>
                            </div>
                        )}
                        {showsLanguages && (
                            <div className={`${styles.fact} ${styles.headlineFact}`}>
                                <dt>{t('project.facts.languages')}</dt>
                                <dd className={styles.factNumber}><CountUp to={project.localeCount} /></dd>
                            </div>
                        )}
                        <div className={styles.fact}>
                            <dt>{t('project.facts.status')}</dt>
                            <dd>{t(`status.${project.status}`)}</dd>
                        </div>
                        <div className={styles.fact}>
                            <dt>{t('project.facts.platforms')}</dt>
                            <dd>{platformLabel(project.platforms)}</dd>
                        </div>
                        <div className={styles.fact}>
                            <dt>{t('project.facts.ownership')}</dt>
                            <dd>{t(`ownership.${project.ownership}`)}</dd>
                        </div>
                        <div className={styles.fact}>
                            <dt>{t('project.facts.stack')}</dt>
                            <dd>
                                <ul className={styles.chips}>
                                    {project.stack.map((technology) => <li key={technology}>{technology}</li>)}
                                </ul>
                            </dd>
                        </div>
                    </dl>
                </div>

                <div className={`page-inner ${styles.highlights}`}>
                    <h2 className={styles.sectionTitle}>{t('project.highlightsTitle')}</h2>
                    <ul className={styles.highlightList}>
                        {copy.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
                    </ul>
                </div>

                {project.websiteSrc && websiteLink && (
                    <div className={`page-inner ${styles.website}`}>
                        <h2 className={styles.sectionTitle}>{t('project.websiteTitle')}</h2>
                        <BrowserFrame src={project.websiteSrc} url={linkHost(websiteLink)} href={websiteLink.href} alt={t('project.websiteAlt', { host: linkHost(websiteLink) })} />
                    </div>
                )}

            </Section>

            <NextProject project={findNeighbourProjects(project.slug).next} />
            <ContactSection />
        </>
    );
}
